import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Honeybadger from '@honeybadger-io/js';
import MemberContext from './MemberContext';
import SnackbarContext from './SnackbarContext';
import {CASH_FUND_TYPE} from '../constants';
import RegistryItemGatewayApi from '../services/RegistryItemGatewayApi';
import CashApi from '../services/CashApi';
import useFinancialAccounts from '../hooks/useFinancialAccounts';
import noop from '../utils/noop';
import {
  getPurchasesCount,
  getTransactionsFromResponse,
  mapTransactionsById,
  mergeCashFundAvailability,
  mergeCashFundPaymentStatus,
} from './utils/purchasers';

const PurchasersContext = createContext({
  currentPurchase: {
    purchaseId: '',
    purchaserName: '',
  },
  hasError: false,
  isFetching: true,
  isOpen: false,
  isShowingCancelConfirm: false,
  onClose: noop,
  onGoBack: noop,
  onOpen: noop,
  onSubmit: noop,
  purchasers: [],
  purchasesCount: 0,
  refetchPurchasers: noop,
  transferTransactions: [],
});

const hasCashFundPurchases = purchasers =>
  Array.isArray(purchasers) &&
  purchasers.some(
    purchaser =>
      Array.isArray(purchaser?.items) &&
      purchaser.items.some(item => item.type === CASH_FUND_TYPE),
  );

export default PurchasersContext;

export const PurchasersContextProvider = ({children}) => {
  const {memberId} = useContext(MemberContext);
  const {showSnackbar} = useContext(SnackbarContext);
  const {
    isEnabled: isFinancialAccountsEnabled,
    isResolved: isFinancialAccountsResolved,
  } = useFinancialAccounts();
  const [currentPurchase, setCurrentPurchase] = useState({
    purchaseId: '',
    purchaserName: '',
  });
  const [hasError, setHasError] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isShowingCancelConfirm, setIsShowingCancelConfirm] = useState(false);
  const [purchasers, setPurchasers] = useState([]);
  const [purchasesCount, setPurchasesCount] = useState(0);
  const [transferTransactions, setTransferTransactions] = useState([]);
  const latestPurchasersRequestId = useRef(0);

  const refetchPurchasers = useCallback(async () => {
    const requestId = latestPurchasersRequestId.current + 1;
    latestPurchasersRequestId.current = requestId;
    const isCurrentRequest = () =>
      latestPurchasersRequestId.current === requestId;

    setIsFetching(true);

    try {
      const response = await RegistryItemGatewayApi.getPurchases();
      let thePurchasers = Array.isArray(response.purchasers)
        ? response.purchasers
        : [];
      let theTransferTransactions = [];

      if (hasCashFundPurchases(thePurchasers)) {
        try {
          const cashPurchases = await CashApi.getPurchasesFundsAvailability();
          thePurchasers = mergeCashFundAvailability(
            thePurchasers,
            cashPurchases,
          );
        } catch (error) {
          Honeybadger.notify(error, 'GetCashFundAvailabilityError');
        }
      }

      if (isFinancialAccountsEnabled) {
        try {
          const transactionsResponse = await CashApi.getAllFinancialAccountTransactions();
          theTransferTransactions = getTransactionsFromResponse(
            transactionsResponse,
          );
          const transactionsById = mapTransactionsById(theTransferTransactions);
          thePurchasers = mergeCashFundPaymentStatus(
            thePurchasers,
            transactionsById,
          );
        } catch (error) {
          Honeybadger.notify(error, 'GetFinancialAccountTransactionsError');
          thePurchasers = mergeCashFundPaymentStatus(thePurchasers);
        }
      }

      if (isCurrentRequest()) {
        setPurchasers(thePurchasers);
        setPurchasesCount(getPurchasesCount(thePurchasers));
        setTransferTransactions(theTransferTransactions);
      }
    } catch (error) {
      if (isCurrentRequest()) {
        setHasError(true);
        Honeybadger.notify(error, 'GetPurchasesError');
      }
    }

    if (isCurrentRequest()) {
      setIsFetching(false);
    }
  }, [isFinancialAccountsEnabled]);

  useEffect(() => {
    if (!isFinancialAccountsResolved) return undefined;

    refetchPurchasers();

    return () => {
      latestPurchasersRequestId.current += 1;
    };
  }, [isFinancialAccountsResolved, refetchPurchasers]);

  const onClose = (isDirty = false) => {
    if (isDirty && isShowingCancelConfirm !== true) {
      setIsShowingCancelConfirm(true);
      return;
    }

    setIsOpen(false);
    setIsShowingCancelConfirm(false);
    setCurrentPurchase({});
  };

  const onGoBack = () => {
    setIsShowingCancelConfirm(false);
  };

  const onOpen = purchase => {
    setIsOpen(true);
    setCurrentPurchase(purchase);
  };

  const onSubmit = async values => {
    if (values.guestName === currentPurchase.purchaserName) {
      onClose();
      return;
    }

    await RegistryItemGatewayApi.updatePurchase({
      memberId,
      purchaseId: currentPurchase.purchaseId,
      item: {specifiedPurchaserName: values.guestName},
    });

    const updatedPurchasers = purchasers.map(({items = [], ...rest}) => {
      let purchaser = {items, ...rest};

      items.forEach(item => {
        if (item.id === currentPurchase.purchaseId) {
          purchaser = {
            ...purchaser,
            specifiedPurchaserName: values.guestName,
          };
        }
      });

      return purchaser;
    });

    setPurchasers(updatedPurchasers);
    onClose();
    showSnackbar('Guest Name Updated');
  };

  return (
    <PurchasersContext.Provider
      value={{
        currentPurchase,
        hasError,
        isFetching,
        isOpen,
        isShowingCancelConfirm,
        onClose,
        onGoBack,
        onOpen,
        onSubmit,
        purchasers,
        purchasesCount,
        refetchPurchasers,
        transferTransactions,
      }}>
      {children}
    </PurchasersContext.Provider>
  );
};

PurchasersContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const usePurchasers = () => useContext(PurchasersContext);
